import { useRef, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ReactTable } from '@crazyegginc/hatch';

import { useFilter } from '../../funnel-filter-context';
import { RowActions } from '../actions';
import { Date } from '/src/components/date-time/Date';

import { ENTER_SITE } from '../../constants';
import { camelToSnake, snakeToCamel } from '/src/utils/string';
import { SORT_ORDER_TYPES } from '../../../_global/constants';

export function FunnelTable({ funnels, queryResult }) {
  const { isInitialLoading, hasNextPage, fetchNextPage } = queryResult;
  const {
    setSort,
    setOrder,
    order: { sort, field },
  } = useFilter();

  const tableRef = useRef(null);

  const columns = useMemo(() => {
    const cols = [
      {
        header: 'Funnel',
        accessorKey: 'name',
        size: 150,
        cell: ({ row }) => {
          const { name, id, visitStep, goalSteps } = row.original;
          return (
            <div className="min-w-0 flex flex-col ">
              <Link className="text-link !underline truncate leading-none" to={`/funnels/edit/${id}`}>
                {name}
              </Link>
              <div className="text-body-2 leading-tight truncate mt-1.25">
                {getStepsDisplayText(visitStep, goalSteps)}
              </div>
            </div>
          );
        },
        meta: {
          align: 'center',
          justify: 'left',
        },
      },
      {
        header: 'Created',
        accessorKey: 'createdAt',
        size: 30,
        cell: ({ row }) => (
          <div className="flex flex-col">
            <Date date={row.original.createdAt} />
            <span className="text-caption">by {row.original.createdBy.name}</span>
          </div>
        ),
        meta: {
          align: 'center',
          justify: 'left',
        },
      },
      {
        header: 'Actions',
        size: 20,
        cell: ({ row }) => (
          <div>
            <RowActions funnel={row.original} />
          </div>
        ),
        meta: {
          align: 'center',
          justify: 'center',
        },
      },
    ];

    return cols;
  }, []);

  const tableSortData = useMemo(() => {
    return {
      id: snakeToCamel(field),
      desc: sort === SORT_ORDER_TYPES.DESC,
    };
  }, [field, sort]);

  const onFetchData = useCallback(
    ({ sorting }) => {
      if (sorting) {
        const nextField = camelToSnake(sorting[0].id).toUpperCase();
        setOrder(nextField);
        const nextSort = sorting[0].desc ? SORT_ORDER_TYPES.DESC : SORT_ORDER_TYPES.ASC;
        setSort(nextSort);
      }
    },
    [setOrder, setSort],
  );

  return (
    <ReactTable
      fetching={isInitialLoading}
      ref={{ tableRef }}
      onFetchData={onFetchData}
      enableSorting={true}
      sorting={tableSortData}
      columns={columns}
      data={funnels}
      rowPadding={true}
      useVirtualization={true}
      hasNextPage={hasNextPage}
      loadNextPage={fetchNextPage}
    />
  );
}

function getStepsDisplayText(visitStep, goalSteps) {
  let result = visitStep ? `${ENTER_SITE} > ` : '';
  result = goalSteps.reduce((acc, g) => acc + `${g.name ?? g.autoName} > `, result);
  return result.replace(/> $/, '');
}
